/**
 * useWebSocketRouter
 * Top-level router for global WebSocket messages
 *
 * Handles messages that need global visibility:
 * - user.joined - User joined team
 * - executor.invoked - Agent execution started
 * - knowledge_base.updated - Knowledge base changed (emits CustomEvent)
 *
 * This hook should be used once at the application level,
 * typically in a WebSocketRouter component.
 */

import { useEffect } from 'preact/hooks';
import { useWebSocketContext } from '../contexts/WebSocketContext';
import type { WebSocketMessage } from '../hooks/useWebSocket';

/**
 * Hook to route global WebSocket messages
 *
 * Subscribes to WebSocket and handles app-level events.
 * Thread-specific messages are ignored (handled by useThreadMessages).
 */
export function useWebSocketRouter() {
  const { subscribe } = useWebSocketContext();

  useEffect(() => {
    const handleMessage = (message: WebSocketMessage) => {
      switch (message.messageType) {
        case 'user.joined':
          console.log('[WebSocketRouter] User joined:', message.payload);
          // TODO: Add UI notification or update user list
          break;

        case 'executor.invoked':
          console.log('[WebSocketRouter] Executor invoked:', message.payload);
          // TODO: Add UI notification for executor start
          break;

        case 'knowledge_base.updated':
          console.log('[WebSocketRouter] Knowledge base updated:', message.payload);
          // Emit custom event for components to listen
          const event = new CustomEvent('knowledge-base-updated', {
            detail: message.payload
          });
          window.dispatchEvent(event);
          break;

        case 'thread.message':
          // No global handling - components handle this via useThreadMessages
          break;

        default:
          console.warn('[WebSocketRouter] Unknown message type:', message.messageType);
      }
    };

    const unsubscribe = subscribe(handleMessage);
    return () => unsubscribe();
  }, [subscribe]);
}
